import { Form, Input, Table, message, Checkbox, Button, Select } from "antd";
import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { serverUrl } from "../../../../../nestserver";
import BackButton from "../../../../../components/buttons/BackButton";

const GRADE_OPTIONS = [
  { label: "Excellent (EX)", value: "EX", color: "#4CAF50" },
  { label: "Very Good (A+)", value: "A+", color: "#66BB6A" },
  { label: "Good (A)", value: "A", color: "#81C784" },
  { label: "Satisfactory (B+)", value: "B+", color: "#FFB300" },
  { label: "Developing (B)", value: "B", color: "#FB8C00" },
];

const MontessoriViewCard = () => {
  const { id } = useParams();
  const [form] = Form.useForm();
  const [cardData, setCardData] = useState(null);
  const [activeTerms, setActiveTerms] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState("Term 1");
  const [loading, setLoading] = useState(false);

  const fetchCard = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await axios({
        method: "get",
        url: `${serverUrl}/montessori-progress-card/${id}?term=${selectedTerm}`,
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      });
      const data = res.data?.result;

      if (activeTerms.length === 0 && data?.template?.active_terms) {
        const terms = data.template.active_terms;
        setActiveTerms(terms);
        const notifiedTerm = data.term_metadata?.find(
          (m) => m.status === "notified" || m.status === "acknowledged",
        );
        if (notifiedTerm) {
          setSelectedTerm(notifiedTerm.term);
        } else if (!terms.includes(selectedTerm) && terms.length > 0) {
          setSelectedTerm(terms[0]);
        }
      }

      const mappedDomains = data?.domains?.map((domain) => ({
        ...domain,
        milestones: domain.milestones.map((m) => {
          const assessment = m.assessments?.find(
            (a) => a.term === selectedTerm,
          );
          return {
            ...m,
            grade: assessment?.grade || "",
            comment: assessment?.comment || "",
          };
        }),
      }));

      const metadata = data?.term_metadata?.find(
        (m) => m.term === selectedTerm,
      );
      const termStatus = metadata?.status || "draft";

      setCardData({
        ...data,
        domains: mappedDomains,
        term_status: termStatus,
        is_locked: termStatus === "acknowledged",
        overall_comment: metadata?.overall_comment || "",
      });

      form.setFieldsValue({
        name: `${data?.student?.first_name} ${data?.student?.last_name}`,
        studentId: data?.student?.unique_id,
        class: data?.student?.class?.name,
        parent: `${data?.student?.parent?.first_name || ""} ${
          data?.student?.parent?.last_name || ""
        }`,
        phone: data?.student?.parent?.phone || "",
        overall_comment: metadata?.overall_comment || "",
        status: termStatus,
        template: data?.template?.name,
      });
    } catch (error) {
      console.error("Error fetching montessori card:", error);
    } finally {
      setLoading(false);
    }
  }, [id, selectedTerm, form, activeTerms.length]);

  useEffect(() => {
    fetchCard();
  }, [id, fetchCard]);

  const onFinish = (values) => {
    axios
      .patch(
        `${serverUrl}/montessori-progress-card/action/acknowledge/${id}?term=${selectedTerm}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        },
      )
      .then((res) => {
        fetchCard();
        message.success(
          res?.data?.message ||
            res?.data?.result?.message ||
            "Acknowledged successfully",
        );
      })
      .catch((err) => {
        if (err?.response?.data?.message) {
          message.error(err?.response?.data?.message);
        }
      });
  };

  const getMilestoneColumns = () => {
    const termColumns = activeTerms.map((term) => ({
      title: term,
      key: `term-${term}`,
      align: "center",
      width: 120,
      render: (_, record) => {
        const isCurrentTerm = term === selectedTerm;
        const assessment = record.assessments?.find((a) => a.term === term);
        const grade = isCurrentTerm ? record.grade : assessment?.grade;

        return (
          <div
            className="font-bold text-base"
            style={{
              color:
                GRADE_OPTIONS.find((o) => o.value === grade)?.color ||
                "#d9d9d9",
            }}
          >
            {grade || "--"}
          </div>
        );
      },
    }));

    return [
      {
        title: "Milestone",
        dataIndex: "description",
        key: "description",
        render: (text) => <div className="text-sm text-gray-700">{text}</div>,
      },
      ...termColumns,
    ];
  };

  return (
    <div className="p-8">
      <div className="bg-white p-10 rounded-2xl shadow-sm">
        <div className="flex justify-between items-center pb-6">
          <div className="flex items-center gap-4">
            <span className="font-semibold text-gray-600">Display Term:</span>
            <Select
              value={selectedTerm}
              onChange={(v) => setSelectedTerm(v)}
              style={{ width: 150 }}
              placeholder="Select Term"
              getPopupContainer={(trigger) => trigger.parentNode}
            >
              {activeTerms.map((term) => (
                <Select.Option key={term} value={term}>
                  {term}
                </Select.Option>
              ))}
            </Select>
            {loading && (
              <span className="text-orange-500 text-sm">Loading...</span>
            )}
            {cardData?.is_locked && (
              <span className="text-green-600 font-bold ml-4 bg-green-50 px-3 py-1 rounded">
                Acknowledged
              </span>
            )}
          </div>
          <BackButton />
        </div>
        <div className="text-2xl text-orange-500 font-semibold pb-1">
          Progress Report Card - {selectedTerm}
        </div>
        <Form
          layout="vertical"
          name="parent-montessori-view"
          form={form}
          autoComplete="off"
          onFinish={onFinish}
        >
          <div className="form-grid-2">
            <Form.Item label="Student Id" name="studentId">
              <Input readOnly />
            </Form.Item>
            <Form.Item label="Name" name="name">
              <Input readOnly />
            </Form.Item>
            <Form.Item label="Class" name="class">
              <Input readOnly />
            </Form.Item>
            <Form.Item label="Parent" name="parent">
              <Input readOnly />
            </Form.Item>
            <Form.Item label="Phone" name="phone">
              <Input readOnly />
            </Form.Item>
            <Form.Item label="Template" name="template">
              <Input readOnly />
            </Form.Item>
          </div>

          {/* Domains & Milestones */}
          <div className="mt-4 mb-6">
            <div className="text-lg font-semibold text-gray-700 mb-4 flex justify-between">
              <span>Categories & Milestones ({selectedTerm})</span>
            </div>
            {cardData?.domains?.map((domain, domainIndex) => (
              <div
                key={domainIndex}
                className="mb-6 border border-gray-200 rounded-lg overflow-hidden"
              >
                <div
                  className="px-5 py-4 border-b flex justify-between items-center rounded-t-lg shadow-sm"
                  style={{ backgroundColor: "#f97316", borderColor: "#ea580c" }}
                >
                  <span
                    className="font-extrabold uppercase tracking-widest text-base"
                    style={{ color: "#ffffff" }}
                  >
                    {domain.name}
                  </span>
                  <span
                    className="text-xs font-bold px-2 py-1 rounded"
                    style={{
                      backgroundColor: "rgba(0,0,0,0.1)",
                      color: "#ffffff",
                    }}
                  >
                    Category
                  </span>
                </div>
                <Table
                  columns={getMilestoneColumns()}
                  dataSource={domain.milestones || []}
                  pagination={false}
                  rowKey={(record, i) => `${domainIndex}-${i}`}
                  size="small"
                />
              </div>
            ))}
          </div>

          {/* General Remarks Grid */}
          <div className="mt-8 mb-8">
            <div className="text-lg font-semibold text-gray-700 mb-4">
              General Remarks
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {activeTerms.map((term) => {
                const isCurrent = term === selectedTerm;
                const metadataForTerm = cardData?.term_metadata?.find(
                  (m) => m.term === term,
                );
                return (
                  <div
                    key={term}
                    className={`p-4 rounded-lg border flex flex-col ${
                      isCurrent
                        ? "border-orange-200 bg-orange-50"
                        : "border-gray-100 bg-gray-50"
                    }`}
                  >
                    <div className="font-bold text-orange-700 mb-2">{term}</div>
                    <div className="text-sm text-gray-600 min-h-[80px] whitespace-pre-wrap">
                      {metadataForTerm?.overall_comment ||
                        "No remarks entered."}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="h-4"></div>

          {/* Grading Norms */}
          <div className="bg-gray-50 p-6 rounded-xl mb-8 border border-gray-100 mt-4">
            <div className="text-sm font-bold text-gray-500 mb-3 uppercase tracking-wider">
              Grading Norms
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {GRADE_OPTIONS.map((opt) => (
                <div key={opt.value} className="flex flex-col">
                  <span
                    style={{ color: opt.color }}
                    className="font-bold text-base"
                  >
                    {opt.value}
                  </span>
                  <span className="text-xs text-gray-500">
                    {opt.label.split("(")[0]}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {cardData &&
            cardData?.term_status !== "acknowledged" &&
            cardData?.term_status === "notified" && (
              <div className="mt-12 p-8 border border-orange-200 rounded-2xl bg-orange-50/30 text-center">
                <Form.Item
                  name="acknowledge"
                  valuePropName="checked"
                  rules={[
                    {
                      validator: (_, value) =>
                        value
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error("Please accept acknowledgement"),
                            ),
                    },
                  ]}
                  className="mb-6"
                >
                  <Checkbox className="text-lg">
                    <span>
                      I acknowledge that the above information provided for{" "}
                      <b>{selectedTerm}</b> is correct
                    </span>
                  </Checkbox>
                </Form.Item>
                <Form.Item className="mb-0">
                  <Button
                    type="primary"
                    size="large"
                    htmlType="submit"
                    className="px-12 h-12 text-lg font-semibold rounded-full bg-orange-500 hover:bg-orange-600 border-none"
                  >
                    Submit Acknowledgment
                  </Button>
                </Form.Item>
              </div>
            )}

          <div className="mt-8 flex gap-4 items-center p-4">
            <span className="font-semibold">{selectedTerm} Status:</span>
            <span className="capitalize text-lg font-bold text-orange-600">
              {cardData?.term_status || "—"}
            </span>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default MontessoriViewCard;
